import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { readUsersMeUsersMeGetOptions } from '@/client/@tanstack/react-query.gen';
import { User } from '@/client';
import { tokenManager } from '@/lib/TokenManager';
import { clearAllAppStorage, safeGetItem } from '@/utils/storage/storageUtils.ts';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/utils/storage/constants.ts';
import { authLogger, logLogin, logLogout } from '@/utils/auth/logging';
import { hasAdminRole } from '@/utils/roleUtils.ts';
import { useTermsCompliance } from '@/hooks/legal/useTermsCompliance.ts';
import { setSentryUser } from '@/utils/sentry/sentryReporter';

export interface AuthContextProps {
	isLoggedIn: boolean;
	userRole: string | null;
	/** Global system/dev admin flag (`User.is_admin`). Not the per-property "admin" role. System admins see all properties as owned, so they have no shared list. */
	isSuperAdmin: boolean;
	token: string | null;
	tokenExpiresAt: number | null;
	user: User | null;
	isUserLoading: boolean;
	isLoggingOut: boolean;
	canFetchOtherData: boolean;

	isTermsCompliant: boolean;
	requiresTermsAcceptance: boolean;

	login: (token: string, refreshToken: string) => void;
	logout: () => void;
	clearAuth: () => void;
}

export const AuthContext = createContext<AuthContextProps>({
	isLoggedIn: false,
	user: null,
	userRole: null,
	isSuperAdmin: false,
	token: null,
	tokenExpiresAt: null,
	isUserLoading: false,
	canFetchOtherData: false,
	isLoggingOut: false,

	isTermsCompliant: false,
	requiresTermsAcceptance: false,

	login: () => {},
	logout: () => {},
	clearAuth: () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const queryClient = useQueryClient();

	// @ts-expect-error variable is not used directly, but forces re-render on token changes
	const [forceUpdate, setForceUpdate] = useState(0);
	const [isLoggingOut, setIsLoggingOut] = useState(false);

	const token = tokenManager.getCurrentToken();
	const tokenExpiresAt = tokenManager.getCurrentExpiry();
	const isLoggedIn = tokenManager.hasValidToken();
	const isTokenExpired = !token || (tokenExpiresAt && Date.now() >= tokenExpiresAt);

	// Subscribe to TokenManager changes
	useEffect(() => {
		const unsubscribe = tokenManager.subscribe(() => {
			setForceUpdate((prev) => prev + 1); // Force re-render when token changes
		});
		return unsubscribe;
	}, []);

	const { data: currentUser, isLoading: isUserLoading } = useQuery({
		...readUsersMeUsersMeGetOptions(),
		enabled: isLoggedIn && !isTokenExpired,
		retry: (failureCount, error) => {
			if (error?.message?.includes('401') || error?.message?.includes('Unauthorized')) {
				return false;
			}
			return failureCount < 3;
		},
	});

	// Terms compliance integration
	const termsCompliance = useTermsCompliance(currentUser);

	// Set Sentry user context when user loads
	useEffect(() => {
		if (currentUser) {
			setSentryUser(currentUser);
		}
	}, [currentUser]);

	// 401 responses are handled by axios response interceptor (refresh + redirect on failure).

	// Determine if other data can be fetched
	const canFetchOtherData = !!(currentUser && isLoggedIn && !isUserLoading && termsCompliance.isCompliant);

	/**
	 * Shared logout execution logic to avoid duplication
	 * Handles the common steps of logout process: state management, cleanup, and redirection
	 */
	const executeLogout = (logMessage: string) => {
		if (isLoggingOut) return; // Prevent multiple simultaneous logouts

		setIsLoggingOut(true);
		clearAuth();

		authLogger.info(logMessage);

		setTimeout(() => {
			window.location.href = '/login';
		}, 100);
	};

	// Only handle deliberate cross-tab logouts - TokenManager handles token updates
	useEffect(() => {
		const performLogout = () => {
			executeLogout('Cross-tab logout detected, redirecting to login');
		};

		const handleStorageChange = (event: StorageEvent) => {
			// Only handle deliberate cross-tab logout detection
			if (event.key === ACCESS_TOKEN_KEY && !event.newValue) {
				const hasRefreshToken = safeGetItem(REFRESH_TOKEN_KEY);

				if (!hasRefreshToken) {
					authLogger.debug('handleStorageChange: deliberate logout detected from another tab');
					// Clear TokenManager to sync with the localStorage state
					tokenManager.clear();
					performLogout(); // Use internal function to avoid recursion
				} else {
					authLogger.debug('handleStorageChange: token temporarily cleared during refresh');
				}
			} else {
				authLogger.debug(`handleStorageChange: ignoring storage event for key ${event.key}`);
			}
		};

		window.addEventListener('storage', handleStorageChange);
		return () => window.removeEventListener('storage', handleStorageChange);
	}, [isLoggingOut]);

	const clearAuth = () => {
		setSentryUser(null);
		clearAllAppStorage();

		queryClient.clear();
		console.info('Silent logout completed');
	};

	const login = (newToken: string, refreshToken: string) => {
		logLogin('email'); // Default login method - could be enhanced with method parameter

		tokenManager.setTokens({ accessToken: newToken, refreshToken });

		try {
			const decoded: { exp?: number } = jwtDecode(newToken);
			const expiresAt = decoded.exp ? decoded.exp * 1000 : null;
			if (expiresAt) {
				authLogger.info(`Token will expire at: ${new Date(expiresAt).toLocaleString()}`);
			}
		} catch (error) {
			console.error('Failed to decode token:', error);
		}

		console.info('User login token stored successfully.');
	};

	const logout = () => {
		logLogout('manual');

		try {
			tokenManager.clear();
			executeLogout('Logging out, redirecting to login');
		} catch {
			setIsLoggingOut(false);
		}
	};

	return (
		<AuthContext.Provider
			value={{
				isLoggedIn,
				user: currentUser || null,
				userRole: null,
				token,
				tokenExpiresAt,
				isUserLoading,
				canFetchOtherData,

				isSuperAdmin: hasAdminRole(currentUser),

				isTermsCompliant: termsCompliance.isCompliant,
				requiresTermsAcceptance: termsCompliance.requiresAcceptance,

				login,
				logout,
				isLoggingOut,
				clearAuth,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const authContext = useContext(AuthContext);
	return {
		...authContext,
		currentUser: authContext.user,
	};
};
